(() => {
  class SearchWordRotator extends HTMLElement {
    connectedCallback() {
      if (this.isInitialized) return;

      this.isInitialized = true;
      this.wordElement = this.querySelector('[data-search-rotating-word]');
      this.words = (this.dataset.searchWords || '')
        .split(',')
        .map((word) => word.trim())
        .filter(Boolean);
      this.currentIndex = Math.max(0, this.words.indexOf(this.wordElement?.textContent.trim()));
      this.characterIndex = this.words[this.currentIndex]?.length || 0;
      this.phase = 'hold';
      this.nextDelay = 3000;
      this.typeDelay = 70;
      this.deleteDelay = 40;
      this.holdDelay = 3000;
      this.gapDelay = 350;
      this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.trigger = this.closest('summary');
      this.action = this.closest('.act_search');
      this.pause = this.pause.bind(this);
      this.resume = this.resume.bind(this);
      this.handleMotionPreference = this.handleMotionPreference.bind(this);
      this.trackSearchLayer = this.trackSearchLayer.bind(this);
      this.updateSearchLayerState = this.updateSearchLayerState.bind(this);

      this.setupSearchLayerObserver();

      if (!this.wordElement || this.words.length < 2) return;

      this.trigger?.addEventListener('mouseenter', this.pause);
      this.trigger?.addEventListener('mouseleave', this.resume);
      this.trigger?.addEventListener('focus', this.pause);
      this.trigger?.addEventListener('blur', this.resume);
      this.reducedMotionQuery.addEventListener?.('change', this.handleMotionPreference);
      this.start();
    }

    disconnectedCallback() {
      this.pause();
      this.trigger?.removeEventListener('mouseenter', this.pause);
      this.trigger?.removeEventListener('mouseleave', this.resume);
      this.trigger?.removeEventListener('focus', this.pause);
      this.trigger?.removeEventListener('blur', this.resume);
      this.reducedMotionQuery?.removeEventListener?.('change', this.handleMotionPreference);
      this.searchLayerObserver?.disconnect();
      this.bodyObserver?.disconnect();
      this.action?.classList.remove('is-search-layer-open');
      this.isInitialized = false;
    }

    start() {
      if (!this.isConnected || this.reducedMotionQuery.matches || this.words.length < 2 || this.timeoutId) return;

      this.timeoutId = window.setTimeout(() => {
        this.timeoutId = undefined;
        this.tick();
      }, this.nextDelay);
    }

    pause() {
      window.clearTimeout(this.timeoutId);
      this.timeoutId = undefined;
    }

    resume() {
      if (this.trigger?.matches(':hover, :focus')) return;
      this.start();
    }

    handleMotionPreference() {
      if (this.reducedMotionQuery.matches) {
        this.pause();
        this.wordElement.textContent = this.words[this.currentIndex];
        return;
      }

      this.start();
    }

    tick() {
      if (!this.isConnected || this.reducedMotionQuery.matches) return;

      if (this.phase === 'hold') {
        this.phase = 'delete';
        this.deleteCharacter();
        return;
      }

      if (this.phase === 'delete') {
        this.deleteCharacter();
        return;
      }

      if (this.phase === 'gap') {
        this.currentIndex = (this.currentIndex + 1) % this.words.length;
        this.characterIndex = 0;
        this.phase = 'type';
        this.typeCharacter();
        return;
      }

      this.typeCharacter();
    }

    deleteCharacter() {
      if (this.characterIndex > 0) {
        this.characterIndex -= 1;
        this.wordElement.textContent = this.words[this.currentIndex].slice(0, this.characterIndex);
      }

      if (this.characterIndex === 0) {
        this.phase = 'gap';
        this.nextDelay = this.gapDelay;
      } else {
        this.nextDelay = this.deleteDelay;
      }

      this.start();
    }

    typeCharacter() {
      const currentWord = this.words[this.currentIndex];
      this.characterIndex += 1;
      this.wordElement.textContent = currentWord.slice(0, this.characterIndex);

      if (this.characterIndex >= currentWord.length) {
        this.phase = 'hold';
        this.nextDelay = this.holdDelay;
      } else {
        this.nextDelay = this.typeDelay;
      }

      this.start();
    }

    setupSearchLayerObserver() {
      this.bodyObserver = new MutationObserver(this.trackSearchLayer);
      this.bodyObserver.observe(document.body, { childList: true, subtree: true });
      this.trackSearchLayer();
    }

    trackSearchLayer() {
      const nextSearchLayer = document.querySelector('.dfd-fullscreen');
      if (nextSearchLayer === this.searchLayer) return;

      this.searchLayerObserver?.disconnect();
      this.searchLayer = nextSearchLayer;

      if (this.searchLayer) {
        this.searchLayerObserver = new MutationObserver(this.updateSearchLayerState);
        this.searchLayerObserver.observe(this.searchLayer, {
          attributes: true,
          attributeFilter: ['class'],
        });
      }

      this.updateSearchLayerState();
    }

    updateSearchLayerState() {
      const isOpen = this.searchLayer?.classList.contains('dfd-animate') || false;
      this.action?.classList.toggle('is-search-layer-open', isOpen);
    }
  }

  if (!customElements.get('search-word-rotator')) {
    customElements.define('search-word-rotator', SearchWordRotator);
  }
})();
